import { createTheme } from '@mui/material/styles';
import { buildTheme } from '../theme';

/* ============================================================
   MUI component theme for the Instructor panel.

   Every MUI Dialog / Button / TextField / Select / Checkbox /
   Switch / Tab etc. rendered inside the Instructor subtree is
   re-skinned here to match the reference panel's modal + form
   primitives, using the exact resolved token values from
   buildTheme(tweaks). React-context scoped via <ThemeProvider>
   in MyRoutes — no global DOM interference.

   Accepts the resolved styled-components theme object (or, for
   back-compat, the string 'light' | 'dark') so the dialogs reskin
   live with the Tweaks panel (accent / density / background…).
   ============================================================ */

const ACCENT_HEX = { indigo: '#5b54e6', violet: '#8b3ff0', blue: '#2f74e0', emerald: '#10977a', amber: '#e0922f' };

export default function createInstructorMuiTheme(themeOrMode = 'light') {
  const T = (typeof themeOrMode === 'string')
    ? buildTheme({ theme: themeOrMode })
    : themeOrMode;
  const mode = T.mode === 'dark' ? 'dark' : 'light';
  const primaryHex = ACCENT_HEX[T.tweaks && T.tweaks.accent] || '#5b54e6';

  return createTheme({
    // accent exposed as parseable hex only so MUI's color math doesn't
    // choke; all visible CSS below uses the exact theme token values.
    palette: { mode, primary: { main: primaryHex } },
    // Our custom modal scrim (primitives.js MODAL_SCRIM_Z_INDEX) sits at 1400.
    // MUI menus/popovers/selects default to 1300, so inside a modal their
    // dropdown would render BEHIND the scrim. Raise MUI modal-layer above it so
    // Select/Menu dropdowns (e.g. domains/subdomains) are visible and clickable.
    zIndex: { modal: 1410, tooltip: 1500, snackbar: 1500 },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: T.fontBody,
      button: { textTransform: 'none', fontWeight: 650 },
    },
    components: {
      MuiBackdrop: {
        styleOverrides: {
          root: { backgroundColor: 'rgba(8,10,20,.46)', backdropFilter: 'blur(6px)' },
          invisible: { backgroundColor: 'transparent', backdropFilter: 'none' },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: T.surface,
            color: T.text,
            borderRadius: 18,
            border: `1px solid ${T.border}`,
            boxShadow: `${T.ring1}, ${T.shadowLg}`,
            backgroundImage: 'none',
            overflow: 'hidden',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            position: 'relative',
            fontFamily: T.fontHead,
            fontSize: 19,
            lineHeight: 1.25,
            fontWeight: 800,
            letterSpacing: '-.01em',
            color: T.text,
            padding: '20px 24px 18px 34px',
            borderBottom: `1px solid ${T.border}`,
            background: `linear-gradient(180deg, ${T.surface2}, ${T.surface})`,
            '&:before': {
              content: '""',
              position: 'absolute',
              left: 18,
              top: 18,
              bottom: 18,
              width: 4,
              borderRadius: 999,
              background: T.accent,
            },
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '22px 24px',
            color: T.text,
            backgroundColor: T.surface,
            '&.MuiDialogContent-dividers': {
              borderTop: 0,
              borderBottom: `1px solid ${T.border}`,
            },
          },
        },
      },
      MuiDialogContentText: {
        styleOverrides: { root: { color: T.textMuted, fontSize: 13.5 } },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '15px 24px',
            borderTop: `1px solid ${T.border}`,
            backgroundColor: T.surface2,
            gap: 10,
            flexWrap: 'wrap',
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: T.rPill,
            fontFamily: T.fontBody,
            fontWeight: 750,
            fontSize: 12,
            minHeight: 34,
            height: 34,
            padding: '0 16px',
            textTransform: 'none',
            lineHeight: 1.2,
            letterSpacing: 0,
            '&.Mui-disabled': { color: T.textSubtle, backgroundColor: T.surface3, borderColor: T.border },
          },
          contained: {
            color: T.btnFg,
            backgroundColor: T.btnBg,
            boxShadow: '0 1px 2px rgba(17,20,38,.18), inset 0 1px 0 rgba(255,255,255,.12)',
            '&:hover': { backgroundColor: T.btnBgHover, boxShadow: T.shadowSm },
          },
          containedPrimary: { backgroundColor: T.btnBg, color: T.btnFg, '&:hover': { backgroundColor: T.btnBgHover } },
          containedSecondary: { backgroundColor: T.accent, color: T.onAccent, '&:hover': { backgroundColor: T.accentStrong } },
          outlined: {
            color: T.accentStrong,
            backgroundColor: T.surface,
            borderColor: T.accentLine,
            boxShadow: 'none',
            '&:hover': { borderColor: T.accent, backgroundColor: T.accentSoft },
          },
          text: { color: T.accentStrong, backgroundColor: 'transparent', '&:hover': { backgroundColor: T.accentSoft } },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { color: T.textMuted, '&:hover': { backgroundColor: T.surface3, color: T.text } },
        },
      },
      // Default to the compact size so floating labels stay vertically centered
      // (and inputs match the reference height) without a hard-coded input padding.
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiFormControl: { defaultProps: { size: 'small' } },
      MuiInputBase: {
        styleOverrides: { root: { fontSize: 13.5, color: T.text, backgroundColor: T.surface2, borderRadius: T.rMd } },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: T.rMd,
            backgroundColor: T.surface2,
            minHeight: 38,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: T.border },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.borderStrong },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.accent, borderWidth: 1 },
            '&.Mui-focused': { backgroundColor: T.surface, boxShadow: `0 0 0 3px ${T.accentSoft}` },
          },
          input: { '&::placeholder': { color: T.textSubtle, opacity: 1 } },
        },
      },
      MuiInputLabel: {
        styleOverrides: { root: { fontSize: 13.5, color: T.textMuted, '&.Mui-focused': { color: T.accentStrong } } },
      },
      MuiFormLabel: {
        styleOverrides: { root: { color: T.text, fontSize: 11.5, fontWeight: 650 } },
      },
      MuiFormHelperText: {
        styleOverrides: { root: { color: T.textSubtle, fontSize: 10.5 } },
      },
      MuiSelect: {
        styleOverrides: { icon: { color: T.textMuted } },
      },
      MuiNativeSelect: {
        styleOverrides: {
          select: {
            minHeight: 38,
            padding: '9px 12px',
            borderRadius: T.rMd,
            backgroundColor: T.surface2,
            color: T.text,
          },
          icon: { color: T.textMuted },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: T.surface, color: T.text, border: `1px solid ${T.border}`,
            borderRadius: T.rMd, boxShadow: T.shadowLg, backgroundImage: 'none',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: { root: { fontSize: 13, '&:hover': { backgroundColor: T.surface2 }, '&.Mui-selected': { backgroundColor: T.accentSoft }, '&.Mui-selected:hover': { backgroundColor: T.accentSoft } } },
      },
      MuiPaper: {
        styleOverrides: { rounded: { borderRadius: T.rMd }, root: { backgroundColor: T.surface, color: T.text, backgroundImage: 'none' } },
      },
      MuiTypography: {
        styleOverrides: {
          root: { fontFamily: T.fontBody },
          h6: { fontFamily: T.fontHead, fontWeight: 780, color: T.text },
          subtitle1: { fontWeight: 700, color: T.text },
          body2: { color: T.textMuted },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          label: { fontSize: 13, fontWeight: 650, color: T.text },
        },
      },
      MuiCheckbox: {
        styleOverrides: { root: { color: T.borderStrong, '&.Mui-checked': { color: T.accent }, '&.MuiCheckbox-indeterminate': { color: T.accent } } },
      },
      MuiRadio: {
        styleOverrides: { root: { color: T.borderStrong, '&.Mui-checked': { color: T.accent } } },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: { '&.Mui-checked': { color: '#fff' }, '&.Mui-checked + .MuiSwitch-track': { backgroundColor: T.accent, opacity: 1 } },
          track: { backgroundColor: T.surface3, opacity: 1, border: `1px solid ${T.border}` },
        },
      },
      MuiSlider: {
        styleOverrides: { root: { color: T.accent }, rail: { backgroundColor: T.surface3, opacity: 1 } },
      },
      MuiTabs: {
        styleOverrides: { indicator: { backgroundColor: T.accent, height: 2 } },
      },
      MuiTab: {
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 650, fontSize: T.fsSm, color: T.textMuted, minHeight: 40, '&.Mui-selected': { color: T.accentStrong } },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: T.border, fontSize: 11.5, color: T.text },
          head: { color: T.textSubtle, fontWeight: 700, fontSize: 10, letterSpacing: '.06em', textTransform: 'uppercase', backgroundColor: T.surface2 },
        },
      },
      MuiTableRow: {
        styleOverrides: { root: { '&:hover': { backgroundColor: T.surface2 } } },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 650, fontSize: 10, borderRadius: 999, backgroundColor: T.surface3, color: T.textMuted },
          outlined: { borderColor: T.border },
        },
      },
      MuiDivider: {
        styleOverrides: { root: { borderColor: T.border } },
      },
      MuiAccordion: {
        styleOverrides: { root: { backgroundColor: T.surface, color: T.text, border: `1px solid ${T.border}`, backgroundImage: 'none', boxShadow: 'none', '&:before': { display: 'none' } } },
      },
      MuiLinearProgress: {
        styleOverrides: { root: { backgroundColor: T.surface3, borderRadius: 999 }, bar: { backgroundColor: T.accent } },
      },
      MuiCircularProgress: {
        styleOverrides: { root: { color: T.accent } },
      },
      MuiTooltip: {
        styleOverrides: { tooltip: { backgroundColor: T.text, color: T.surface, fontSize: 11, borderRadius: 8 } },
      },
      MuiListItemButton: {
        styleOverrides: { root: { borderRadius: T.rSm, '&:hover': { backgroundColor: T.surface2 }, '&.Mui-selected': { backgroundColor: T.accentSoft } } },
      },
    },
  });
}
